import { useFormSubmission } from '../../contexts/form-submission-context';
import { CheckCircle2, XCircle, Trophy, Target } from 'lucide-react';
import { cn } from '@shondalai/ui-commons';
import { Card, CardContent, useLanguage } from '@shondalai/ui-commons';

export function QuizResultsDisplay() {
  const { quizResults, form } = useFormSubmission();
  const { t } = useLanguage();

  if (!quizResults || !form) return null;

  const passed = quizResults.passed !== undefined 
    ? quizResults.passed 
    : quizResults.percentage >= (quizResults.passingScore || 70);

  const formatAnswer = (answer: any): string => {
    if (answer === null || answer === undefined) return t('COM_EASYFORMS_NOT_ANSWERED', 'Not answered');
    if (Array.isArray(answer)) return answer.join(', ');
    if (typeof answer === 'object') return JSON.stringify(answer);
    return String(answer);
  };

  return (
    <div className="mx-auto space-y-6">
      {/* Overall Results Header */}
      <Card className={cn(
        "border-2",
        passed ? "border-green-500/20 bg-green-500/5" : "border-yellow-500/20 bg-yellow-500/5"
      )}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={cn(
                "w-16 h-16 rounded-full flex items-center justify-center",
                passed ? "bg-green-500/10 text-green-600" : "bg-yellow-500/10 text-yellow-600"
              )}>
                {passed ? <Trophy className="w-8 h-8" /> : <Target className="w-8 h-8" />}
              </div>
              <div>
                <h3 className="text-2xl font-bold">
                  {passed ? t('COM_EASYFORMS_CONGRATULATIONS', 'Congratulations!') : t('COM_EASYFORMS_GOOD_EFFORT', 'Good Effort!')}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {passed 
                    ? t('COM_EASYFORMS_YOU_PASSED_QUIZ', 'You passed the quiz!')
                    : `${t('COM_EASYFORMS_PASSING_SCORE', 'Passing score')}: ${quizResults.passingScore}%`
                  }
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-4xl font-bold">{quizResults.percentage}%</div>
              <div className="text-sm text-muted-foreground">
                {quizResults.score} / {quizResults.maxScore} {t('COM_EASYFORMS_POINTS', 'points')}
              </div>
              {quizResults.grade && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary/10 text-primary">
                    {t('COM_EASYFORMS_GRADE', 'Grade')}: {quizResults.grade}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Overall Feedback */}
          {quizResults.feedback && (
            <div className="mt-4 p-4 bg-muted/30 rounded-lg">
              <p className="text-sm">{quizResults.feedback}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detailed Answer Review */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold">{t('COM_EASYFORMS_ANSWER_REVIEW', 'Answer Review')}</h4>

        {quizResults.answers.map((answer, index) => (
          <Card key={index} className={cn(
            "border-l-4",
            answer.isCorrect ? "border-l-green-500" : "border-l-red-500"
          )}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                {/* Correct/Incorrect Icon */}
                <div className="mt-0.5">
                  {answer.isCorrect ? (
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-600" />
                  )}
                </div>

                {/* Question and Answer Details */}
                <div className="flex-1 space-y-2">
                  {/* Question */}
                  <div>
                    <p className="font-medium text-foreground">
                      {index + 1}. {answer.question}
                    </p>
                  </div>

                  {/* Your Answer */}
                  <div>
                    <span className="text-xs text-muted-foreground">{t('COM_EASYFORMS_YOUR_ANSWER', 'Your Answer')}: </span>
                    <span className={cn(
                      "text-sm font-medium",
                      answer.isCorrect ? "text-green-600" : "text-red-600"
                    )}>
                      {formatAnswer(answer.userAnswer)}
                    </span>
                  </div>

                  {/* Correct Answer (if wrong) */}
                  {!answer.isCorrect && (
                    <div>
                      <span className="text-xs text-muted-foreground">{t('COM_EASYFORMS_CORRECT_ANSWER', 'Correct Answer')}: </span>
                      <span className="text-sm font-medium text-green-600">
                        {formatAnswer(answer.correctAnswer)}
                      </span>
                    </div>
                  )}

                  {/* Points */}
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{t('COM_EASYFORMS_POINTS_LABEL', 'Points')}: {answer.points} / {answer.maxPoints}</span>
                  </div>

                  {/* Explanation */}
                  {answer.explanation && (
                    <div className="mt-2 p-3 bg-muted/30 rounded-md">
                      <p className="text-xs text-muted-foreground mb-1 font-medium">
                        {t('COM_EASYFORMS_EXPLANATION', 'Explanation')}:
                      </p>
                      <p className="text-sm">{answer.explanation}</p>
                    </div>
                  )}

                  {/* Feedback */}
                  {answer.feedback && (
                    <div className="mt-2">
                      <p className="text-sm italic text-muted-foreground">
                        {answer.feedback}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
